import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Store as StoreIcon } from 'lucide-react';

const Stores = () => {
  const { token, user } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: ''
  });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (error) {
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingStore
        ? `${process.env.REACT_APP_BACKEND_URL}/api/stores/${editingStore.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/stores`;

      const response = await fetch(url, {
        method: editingStore ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingStore ? 'Store updated' : 'Store created');
        setDialogOpen(false);
        resetForm();
        fetchStores();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Operation failed');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this store?')) return;

    try {
      let response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Store deleted');
        fetchStores();
        return;
      }

      const data = await response.json().catch(() => ({}));

      // If the backend is blocking because of child data, offer force delete
      if (response.status === 400 && data.detail && /force delete/i.test(data.detail)) {
        const proceed = window.confirm(
          `${data.detail}\n\nFORCE DELETE: this will permanently delete the store AND every category, subcategory, product and variant inside it. This cannot be undone.\n\nContinue?`
        );
        if (!proceed) return;

        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores/${id}?force=true`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json().catch(() => ({}));
          const c = result.cascade;
          toast.success(
            c ? `Store deleted (removed ${c.categories} categories, ${c.products} products, ${c.variants} variants)` : 'Store deleted'
          );
          fetchStores();
          return;
        }
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || 'Failed to force delete store');
        return;
      }

      toast.error(data.detail || 'Failed to delete store');
    } catch (error) {
      toast.error('Network error');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', phone: '' });
    setEditingStore(null);
  };

  const openEditDialog = (store) => {
    setEditingStore(store);
    setFormData({
      name: store.name,
      address: store.address || '',
      phone: store.phone || ''
    });
    setDialogOpen(true);
  };

  if (user?.role !== 'admin') {
    return (
      <div className="text-center p-8">
        <StoreIcon className="mx-auto mb-4 text-destructive" size={48} />
        <h2 className="font-heading font-bold text-2xl mb-2">Access Denied</h2>
        <p className="text-muted-foreground font-body">Only administrators can manage stores.</p>
      </div>
    );
  }

  return (
    <div data-testid="stores-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight" data-testid="stores-title">Stores</h1>
          <p className="text-muted-foreground font-body mt-2">Manage your store locations</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-store-btn">
              <Plus size={18} strokeWidth={1.5} className="mr-2" />
              Add Store
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="store-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-2xl">
                {editingStore ? 'Edit Store' : 'Add New Store'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Store Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1.5"
                  data-testid="store-name-input"
                />
              </div>

              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="mt-1.5"
                  data-testid="store-address-input"
                />
              </div>

              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1.5"
                  data-testid="store-phone-input"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="store-submit-btn">
                  {editingStore ? 'Update' : 'Create'} Store
                </Button>
                <Button type="button" variant="secondary" onClick={() => { setDialogOpen(false); resetForm(); }} data-testid="store-cancel-btn">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stores Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stores-grid">
        {loading ? (
          <div className="col-span-full text-center p-8 text-muted-foreground font-mono">Loading...</div>
        ) : stores.length === 0 ? (
          <div className="col-span-full text-center p-8 text-muted-foreground font-body">No stores found. Create your first store!</div>
        ) : (
          stores.map((store) => (
            <div
              key={store.id}
              className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors duration-300"
              data-testid={`store-card-${store.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <StoreIcon size={20} strokeWidth={1.5} className="text-primary" />
                    <h3 className="font-heading font-bold text-xl">{store.name}</h3>
                  </div>
                  {store.address && (
                    <p className="text-sm text-muted-foreground font-body mb-1">{store.address}</p>
                  )}
                  {store.phone && (
                    <p className="text-sm text-muted-foreground font-mono">{store.phone}</p>
                  )}
                </div>
                <div className="flex gap-1 ml-2">
                  <button
                    onClick={() => openEditDialog(store)}
                    className="p-2 hover:bg-secondary rounded transition-colors"
                    data-testid={`edit-store-${store.id}`}
                  >
                    <Pencil size={16} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => handleDelete(store.id)}
                    className="p-2 hover:bg-destructive/10 text-destructive rounded transition-colors"
                    data-testid={`delete-store-${store.id}`}
                  >
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
              <div className="pt-4 border-t border-border">
                <p className="font-mono text-xs text-muted-foreground">
                  Created: {new Date(store.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Stores;
